import {
  useRevenueByTransactionTypeReport,
  useRevenueByTransactionTypeReportCsv,
} from "@gymflow/api";
import {
  DATE_FORMAT,
  PARAMETER_DATE_FORMAT,
  PARAMETER_DATE_FORMAT_WITHOUT_TZ,
} from "@gymflow/common";
import { downloadCsv, formatCurrency } from "@gymflow/helpers";
import { TransactionReportItemDTO, TransactionType } from "@gymflow/types";
import { createColumnHelper, SortingState } from "@tanstack/react-table";
import moment from "moment-timezone";
import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { useHistory, useLocation } from "react-router";

import { ReportPagination } from "../../components/molecules";
import { Table } from "../../components/organisms";
import { Report } from "../../components/organisms/Report";
import useQueryParam from "../../hooks/useQueryParam";
import { useClubSettings } from "../../providers";
import useGymflowModels from "../../store";
import { formatDateRange, getDefaultDateRange } from "./helpers/dateHelpers";

export function RevenueTransaction() {
  const [transactionType] = useQueryParam("type", "string") as [
    TransactionType,
  ];
  const history = useHistory();
  const { api } = useGymflowModels();

  const location = useLocation();
  const searchParams = qs.parse(location.search, { ignoreQueryPrefix: true });
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState(25);
  const [currentSort, setCurrentSort] = useState<SortingState>([]);

  const {
     featureFlags, club, properties ,
  } = useClubSettings();

  const [dates, setDates] = useQueryParam("dates", "string") as any;
  const defaultDates = useMemo(
    () => ({
      startDate: moment().format(PARAMETER_DATE_FORMAT),
      endDate: moment().endOf("month").format(PARAMETER_DATE_FORMAT),
    }),
    [],
  );

  const defaultRange = useMemo(
    () => getDefaultDateRange(club.timezone),
    [club.timezone],
  );

  useEffect(() => {
    if (featureFlags.PORTAL_NEW_REPORTS_DATE_PICKER && !dates) {
      setDates(defaultRange);
    }
    if (!featureFlags.PORTAL_NEW_REPORTS_DATE_PICKER && !dates) {
      setDates(defaultDates);
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isFetching } = useRevenueByTransactionTypeReport({
    api,
    dateFrom: dates?.startDate,
    dateTo: dates?.endDate,
    tz: club.timezone,
    limit: pageSize,
    page: currentPage,
    transactionType: transactionType as any,
  });

  const downloadCsvMutation = useRevenueByTransactionTypeReportCsv({
    api,
    tz: club.timezone,
  });

  const columnHelper = createColumnHelper<TransactionReportItemDTO>();
  const columnsDefinition = [
    columnHelper.accessor("date", {
      cell: (info) => (
        <div className="text-gray-600">
          {moment(info.getValue(), PARAMETER_DATE_FORMAT_WITHOUT_TZ).format(
            properties.dateFormat,
          )}
        </div>
      ),
      header: () => <div>Date</div>,
      enableSorting: false,
    }),
    columnHelper.accessor("name", {
      cell: (info) => {
        return <div className="text-gray-600">{info.getValue()}</div>;
      },
      header: "Name",
      enableSorting: false,
    }),
    columnHelper.accessor("amount", {
      cell: (info) => (
        <div className="text-gray-600">
          {formatCurrency(info.getValue(), club.defaultCurrency)}
        </div>
      ),
      header: "Amount",
      footer: () =>
        `Total: ${
          data?.total ? formatCurrency(data.total, club.defaultCurrency) : ""
        }`,
      enableSorting: false,
    }),
  ];

  const handleDateRangeChange = useCallback(
    (newDate: any) => {
      if (newDate) {
        setDates({
          startDate: moment(newDate.startDate, DATE_FORMAT).format(
            PARAMETER_DATE_FORMAT_WITHOUT_TZ,
          ),
          endDate: moment(newDate.endDate, DATE_FORMAT)
            .endOf("day")
            .format(PARAMETER_DATE_FORMAT_WITHOUT_TZ),
        });
      }
    },
    [setDates],
  );

  const onDateSelectionApply = useCallback(
    (
      newDate: DateRange | { startDate: string; endDate: string } | undefined,
    ) => {
      setDates(formatDateRange(newDate));
    },
    [setDates],
  );

  return (
    <Report
      title={
        (
          {
            PENDING_AUTHORIZATION: "Awaiting Authorizations",
            REFUND_DEBIT: "Refunds",
            WRITE_OFF: "Write-Offs",
            MANUAL_CREDIT: "Credits Applied",
            CREDIT: "", // these reports do no exist
            DEBIT: "",
          } as { [key in TransactionType]: string }
        )[transactionType]
      }
      description={
        (
          {
            PENDING_AUTHORIZATION:
              "List of all payments awaiting authorization within the date range specified. Any payments not authorized by users will not be collected.",
            REFUND_DEBIT:
              "List of all refunds processed by staff back to users within the specified date period.",
            WRITE_OFF:
              "List of all invoices written off by staff due to non-payment within the specified date period.",
            MANUAL_CREDIT:
              "List of all credits applied to invoices by staff within the specified date period.",
            DEBIT: "", // these reports do no exist
            CREDIT: "",
          } as { [key in TransactionType]: string }
        )[transactionType]
      }
      onDownloadClick={async () => {
        const data = await downloadCsvMutation.mutateAsync({
          transactionType: transactionType as any,
          dateFrom: dates?.startDate,
          dateTo: dates?.endDate,
        });
        downloadCsv(
          data,
          `${
            (
              {
                PENDING_AUTHORIZATION: "AwaitingAuthorizations",
                REFUND_DEBIT: "Refunds",
                WRITE_OFF: "WriteOffs",
                MANUAL_CREDIT: "CreditsApplied",
                CREDIT: "", // these reports do no exist
                DEBIT: "",
              } as { [key in TransactionType]: string }
            )[transactionType]
          }.csv`,
        );
      }}
      onDateSelectionApply={onDateSelectionApply}
      handleDateRangeChange={handleDateRangeChange}
      dateRange={
        dates?.startDate && dates?.endDate
          ? featureFlags.PORTAL_NEW_REPORTS_DATE_PICKER
            ? {
                from: new Date(dates.startDate),
                to: new Date(dates.endDate),
              }
            : {
                startDate: moment(
                  dates.startDate,
                  PARAMETER_DATE_FORMAT_WITHOUT_TZ,
                ).format(DATE_FORMAT),
                endDate: moment(
                  dates.endDate,
                  PARAMETER_DATE_FORMAT_WITHOUT_TZ,
                ).format(DATE_FORMAT),
              }
          : undefined
      }
      close={() => {
        history.push({
          pathname: searchParams["backRoute"] as string,
          search: qs.stringify({
            dates: {
              startDate: moment(
                dates.startDate,
                PARAMETER_DATE_FORMAT_WITHOUT_TZ,
              ).format(DATE_FORMAT),
              endDate: moment(
                dates.endDate,
                PARAMETER_DATE_FORMAT_WITHOUT_TZ,
              ).format(DATE_FORMAT),
            },
          }),
        });
      }}
      table={
        <>
          <Table
            data={data?.data.content ?? []}
            columns={columnsDefinition}
            pageCount={data?.data.totalPages}
            pageIndex={data?.data.number}
            onSortingChange={setCurrentSort}
            sort={currentSort}
            isFetching={isFetching}
            pageSize={pageSize}
            rowClassName="h-14"
          />
          <div className="border-t border-gray-300">
            <ReportPagination
              pageCount={data?.data.totalPages as number}
              currentPage={data?.data.number as number}
              onPageChange={(newPage) => {
                setCurrentPage(newPage);
              }}
              pageSize={pageSize}
              setPageSize={(newValue) => {
                setPageSize(newValue);
                setCurrentPage(0);
              }}
            />
          </div>
        </>
      }
    />
  );
}
